/**
 * Lecture Service - Frontend
 * @description Service for lecture-related API operations
 * Optimized for operational dashboard flow
 * Uses mappers to transform backend data (shortened fields) to frontend data (full fields)
 */

import { api, REDIS_API_URL } from '../config';
import { ApiResponse, LectureResponse, LectureQueryParams, CreateLectureData } from '@/types';
import { BackendApiResponse, BackendLecture } from '@/types/backend.types';
import { LectureMapper } from '@/mappers/lecture.mapper';
import { ApiMapper } from '@/mappers/api.mapper';

export const lectureService = {
  /**
   * Get all lectures with optional pagination and filters
   * For admin users or general lecture listing
   */
  async getAll(params?: LectureQueryParams): Promise<ApiResponse<LectureResponse[]>> {
    const response = await api.get<BackendApiResponse<BackendLecture[]>>('/lectures', { params });
    return ApiMapper.mapResponseArray(response.data, LectureMapper.toFrontend);
  },

  /**
   * Get lecture by ID
   * For lecture details if needed
   */
  async getById(id: string): Promise<ApiResponse<LectureResponse>> {
    const response = await api.get<BackendApiResponse<BackendLecture>>(`/lectures/${id}`);
    return ApiMapper.mapResponse(response.data, LectureMapper.toFrontend);
  },

  /**
   * ⭐ CORE: Get lectures by operation ID
   * For Tabla 3: Lectures of a specific operation
   * Called from LecturesDetailPage when user selects an operation
   */
  async getByOperationId(
    operationId: string,
    params?: LectureQueryParams
  ): Promise<ApiResponse<LectureResponse[]>> {
    const response = await api.get<BackendApiResponse<BackendLecture[]>>(
      `/lectures/operation/${operationId}`,
      { params }
    );
    return ApiMapper.mapResponseArray(response.data, LectureMapper.toFrontend);
  },

  /**
   * ⭐ CORE: Get lectures by device ID
   * For Tabla 4: All lectures of a specific device (SUPER TABLA)
   * Called from AllLecturesPage when user wants to see all lectures for a device
   */
  async getByDeviceId(
    deviceId: string,
    params?: LectureQueryParams
  ): Promise<ApiResponse<LectureResponse[]>> {
    const response = await api.get<BackendApiResponse<BackendLecture[]>>(
      `/lectures/device/${deviceId}`,
      {
        params,
      }
    );
    return ApiMapper.mapResponseArray(response.data, LectureMapper.toFrontend);
  },

  /**
   * 🚀 SUPER FEATURE: Get lectures by user ID (all devices)
   * For Tabla 4 ULTIMATE: All lectures from ALL devices associated to the user
   * This would require a new endpoint in the backend or client-side aggregation
   * For now, we'll implement client-side aggregation using existing endpoints
   */
  async getByUserId(
    userId: string,
    params?: LectureQueryParams
  ): Promise<ApiResponse<LectureResponse[]>> {
    // Strategy:
    // 1. Get user's devices from deviceService
    // 2. Get lectures for each device
    // 3. Aggregate and sort

    try {
      // Import deviceService here to avoid circular dependencies
      const { deviceService } = await import('./deviceService');

      // Get user's devices
      const devicesResponse = await deviceService.getByUserId(userId);
      if (!devicesResponse.success || !devicesResponse.data) {
        return {
          success: false,
          error: {
            code: 'DEVICES_NOT_FOUND',
            message: 'Failed to get user devices',
          },
          message: 'Failed to get user devices',
        };
      }

      // Get lectures for each device
      const lecturePromises = devicesResponse.data.map((device) =>
        this.getByDeviceId(device.id!, params)
      );

      const lectureResponses = await Promise.all(lecturePromises);

      // Aggregate all lectures
      const allLectures: LectureResponse[] = [];
      lectureResponses.forEach((response) => {
        if (response.success && response.data) {
          allLectures.push(...response.data);
        }
      });

      // Sort by timestamp (most recent first)
      allLectures.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());

      // Apply client-side pagination if needed
      let paginatedLectures = allLectures;
      if (params?.page && params?.limit) {
        const startIndex = (params.page - 1) * params.limit;
        const endIndex = startIndex + params.limit;
        paginatedLectures = allLectures.slice(startIndex, endIndex);
      }

      return {
        success: true,
        data: paginatedLectures,
        message: `Found ${allLectures.length} lectures from ${devicesResponse.data.length} devices`,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'LECTURES_FETCH_ERROR',
          message: `Failed to get lectures for user: ${error}`,
        },
        message: `Failed to get lectures for user: ${error}`,
      };
    }
  },

  /**
   * Get lectures by serial key
   * For specific lecture lookup
   */
  async getBySerialKey(
    serialKey: string,
    params?: LectureQueryParams
  ): Promise<ApiResponse<LectureResponse[]>> {
    const response = await api.get<BackendApiResponse<BackendLecture[]>>(
      `/lectures/serial/${serialKey}`,
      { params }
    );
    return ApiMapper.mapResponseArray(response.data, LectureMapper.toFrontend);
  },

  /**
   * Create new lecture
   * For future functionality if needed
   */
  async create(lectureData: CreateLectureData): Promise<ApiResponse<LectureResponse>> {
    // ORIGINAL: Direct to backend
    // const backendData = ApiMapper.mapRequestData(lectureData, LectureMapper.toBackend);
    // const response = await api.post<BackendApiResponse<BackendLecture>>('/lectures', backendData);
    // return ApiMapper.mapResponse(response.data, LectureMapper.toFrontend);

    // NEW: Through Queue Service (intermediary)
    try {
      // Get queue service URL from configuration
      const queueServiceUrl = REDIS_API_URL;

      // Wrap lecture data in the nested structure expected by the queue service
      const queuePayload = {
        data: {
          lecture: lectureData,
          attempt: 1,
        },
      };

      // Send to queue service with nested structure
      const response = await window.fetch(`${queueServiceUrl}/api/lectures`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(queuePayload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        return {
          success: true,
          data: {
            id: result.data?.id || 'queued',
            skey: lectureData.skey,
            opId: lectureData.opId,
            ts: lectureData.ts,
            tz: lectureData.tz,
            coords: lectureData.coords,
            type: lectureData.type,
            ang: lectureData.ang,
            lim: lectureData.lim,
            seq: lectureData.seq,
            order: lectureData.order,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          } as LectureResponse,
          message: result.message || 'Lecture queued successfully',
        };
      } else {
        return {
          success: false,
          error: {
            code: 'QUEUE_ERROR',
            message: result.message || 'Failed to queue lecture',
          },
          message: result.message || 'Failed to queue lecture',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: `Network error: ${error}`,
        },
        message: `Network error: ${error}`,
      };
    }
  },

  /**
   * Create multiple lectures in batch
   * For bulk insertion using the new queue service batch endpoint
   */
  async createBatch(lectures: CreateLectureData[]): Promise<
    ApiResponse<{
      batchId: string;
      message: string;
      count: number;
    }>
  > {
    try {
      console.log('🚀 [FRONTEND] Starting batch creation for', lectures.length, 'lectures');

      // Get queue service URL from configuration
      const queueServiceUrl = REDIS_API_URL;
      console.log('🚀 [FRONTEND] Queue service URL:', queueServiceUrl);

      // Convert lectures to the format expected by the batch endpoint
      const batchLectures = lectures.map((lecture) => ({
        skey: lecture.skey,
        ts: lecture.ts,
        tz: lecture.tz,
        coords: lecture.coords,
        type: lecture.type,
        ang: lecture.ang,
        lim: lecture.lim,
        seq: lecture.seq,
        order: lecture.order,
      }));

      // Send to queue service batch endpoint
      console.log('🚀 [FRONTEND] Sending request to:', `${queueServiceUrl}/api/batch_lectures`);
      console.log('🚀 [FRONTEND] Request body:', JSON.stringify(batchLectures, null, 2));

      const controller = new window.AbortController();
      const timeoutId = window.setTimeout(() => {
        console.log('⏰ [FRONTEND] Request timeout after 2 minutes');
        controller.abort();
      }, 120000); // 2 minutes timeout

      console.log('🚀 [FRONTEND] Making fetch request...');
      const response = await window.fetch(`${queueServiceUrl}/api/batch_lectures`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(batchLectures),
        signal: controller.signal,
      });

      window.clearTimeout(timeoutId);
      console.log('🚀 [FRONTEND] Response received:', response.status, response.statusText);

      const result = await response.json();

      if (response.ok && result.success) {
        return {
          success: true,
          data: {
            batchId: result.batchId,
            message: result.message || 'Batch processed successfully',
            count: lectures.length,
          },
          message: `Batch of ${lectures.length} lectures queued successfully`,
        };
      } else {
        return {
          success: false,
          error: {
            code: 'BATCH_QUEUE_ERROR',
            message: result.error?.message || 'Failed to queue batch',
          },
          message: result.error?.message || 'Failed to queue batch',
        };
      }
    } catch (error: unknown) {
      console.error('Error creating batch lectures:', error);

      // Handle timeout specifically
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: {
            code: 'BATCH_TIMEOUT_ERROR',
            message: 'Batch processing timed out after 2 minutes',
          },
          message:
            'Batch processing timed out. Please try with fewer lectures or check server status.',
        };
      }

      return {
        success: false,
        error: {
          code: 'BATCH_NETWORK_ERROR',
          message: `Network error: ${error instanceof Error ? error.message : String(error)}`,
        },
        message: `Network error: ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  },

  /**
   * Update lecture
   * For future functionality if needed
   */
  async update(
    id: string,
    lectureData: Partial<LectureResponse>
  ): Promise<ApiResponse<LectureResponse>> {
    const backendData = ApiMapper.mapRequestData(lectureData, LectureMapper.toBackend);
    const response = await api.put<BackendApiResponse<BackendLecture>>(
      `/lectures/${id}`,
      backendData
    );
    return ApiMapper.mapResponse(response.data, LectureMapper.toFrontend);
  },

  /**
   * Delete lecture
   * For future functionality if needed
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<BackendApiResponse<void>>(`/lectures/${id}`);

    if (response.data.success) {
      return {
        success: true,
        data: undefined as void,
        message: response.data.message,
      };
    } else {
      return {
        success: false,
        error: response.data.error!,
        message: response.data.message,
      };
    }
  },
};
